import {
  memo, useCallback, useEffect, useRef
} from 'react';
import PropTypes from 'prop-types';
import Option from './Option';
import Gradient, { GRADIENT_POSITION_LEFT } from '@/components/app/Gradient';
import LayoutWrapperHorizontal from '@/components/component-library/LayoutWrapperHorizontal';
import { classes } from '@/helpers/styling';

const GRADIENT_STYLE = {
  height: 'calc(100% - 1px)',
  top: '-10px',
  width: '10px'
};

const OptionSelectorHorizontalScrolling = ({
  id,
  options,
  onClickOption,
  selectedId,
  className,
  listClassName,
  optionClassName,
  firstOptionFixed,
  leftScrollOffset,
  OptionComponent
}) => {
  const scrollingRef = useRef();

  const generateOptionId = useCallback((optionId) => `horizontal-scroller-option-${ id }-${ optionId }`, [id]);

  const handleSelectOption = (option) => {
    if (onClickOption) {
      onClickOption(option);
    }
  };

  const handleScrollToOption = useCallback((optionId) => {
    const elementToScrollTo = document.getElementById(generateOptionId(optionId));

    scrollingRef.current.scrollTo({
      left: elementToScrollTo ? elementToScrollTo.offsetLeft - scrollingRef.current.offsetLeft - leftScrollOffset : 0,
      behavior: 'smooth'
    });
  }, [leftScrollOffset, generateOptionId]);

  useEffect(() => {
    // Scroll to selected id
    handleScrollToOption(selectedId);
  }, [selectedId, handleScrollToOption]);

  const selectedOption = options.find(({ id: optionId }) => optionId === selectedId);
  const selectedOptionId = (selectedOption || {}).id;

  const firstOption = options[0] || {};
  const optionsToRender = (firstOptionFixed ? options.slice(1) : options).map(({
    id: optionId, value, href
  }, i) => (
    <li
      className={ classes(
        'scrolling-selector-h__option-wrapper',
        optionClassName,
        {
          'scrolling-selector-h__option-wrapper-first': firstOptionFixed && i === 0,
          '--disable-padding': firstOptionFixed
        }
      ) }
      id={ generateOptionId(optionId) }
      key={ `id-${ optionId }` }
    >
      <Option
        href={ href }
        id={ optionId }
        onClick={ () => handleSelectOption({
          id: optionId,
          value
        }) }
        OptionComponent={ OptionComponent }
        selected={ optionId === selectedOptionId }
        value={ value }
      />
    </li>
  ));

  return (
    <div className={ classes('scrolling-selector-h', className) }>
      <LayoutWrapperHorizontal
        className='scrolling-selector-h__padding'
        disable={ !firstOptionFixed }
        isRow
      >
        { firstOptionFixed && (
          <div className='scrolling-selector-h__option-wrapper scrolling-selector-h__option-fixed'>
            <Option
              href={ firstOption.href }
              id={ firstOption.id }
              key={ firstOption.id }
              onClick={ () => handleSelectOption({
                id: firstOption.id,
                value: firstOption.value
              }) }
              OptionComponent={ OptionComponent }
              selected={ firstOption.id === selectedOptionId }
              value={ firstOption.value }
            />
          </div>
        ) }
        <LayoutWrapperHorizontal
          className='scrolling-selector-h__list-wrapper'
          disable={ !firstOptionFixed }
          isRow
        >
          { firstOptionFixed && (
            <Gradient
              extraBreadth={ false }
              gradientBreadth='50%'
              position={ GRADIENT_POSITION_LEFT }
              style={ GRADIENT_STYLE }
            />
          ) }
          <ul
            className={ classes('scrolling-selector-h__list', listClassName) }
            ref={ scrollingRef }
          >
            { optionsToRender }
          </ul>
        </LayoutWrapperHorizontal>
      </LayoutWrapperHorizontal>
    </div>
  );
};

OptionSelectorHorizontalScrolling.propTypes = {
  id: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired
    })
  ),
  className: PropTypes.string,
  listClassName: PropTypes.string,
  optionClassName: PropTypes.string,
  selectedId: PropTypes.string,
  onClickOption: PropTypes.func,
  firstOptionFixed: PropTypes.bool,
  leftScrollOffset: PropTypes.number,
  OptionComponent: PropTypes.element
};

OptionSelectorHorizontalScrolling.defaultProps = {
  options: [],
  className: '',
  listClassName: '',
  optionClassName: '',
  selectedId: undefined,
  onClickOption: undefined,
  firstOptionFixed: false,
  leftScrollOffset: 0,
  OptionComponent: undefined
};

export default memo(OptionSelectorHorizontalScrolling);